import { Downloading, Print } from '@mui/icons-material';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import api from '../../api';
import { presentUuid } from '../../helpers/formatters';
import { generatePDF } from './export_pdf';
import OrderDetailsBody from './OrderDetailsBody';

/**
 * Carrega e exibe os detalhes de uma venda pelo UUID explícito do pedido (gRPC GetOrder).
 */
export default function OrderDetailsPanel({ orderId, title, showPrintButton = true }) {
    const [order, setOrder] = useState(null);
    const [transaction, setTransaction] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [loadingPDF, setLoadingPDF] = useState(false);

    const load = useCallback(() => {
        if (!orderId) {
            setOrder(null);
            setTransaction(null);
            setDidNotLoad(false);
            setIsLoading(false);
            return undefined;
        }

        let cancelled = false;
        setIsLoading(true);
        setDidNotLoad(false);
        setOrder(null);
        setTransaction(null);

        api.get(`orders/${orderId}`)
            .then((response) => {
                if (cancelled) {
                    return undefined;
                }
                if (response.data?.success && response.data?.data) {
                    const orderData = response.data.data;
                    setOrder(orderData);
                    return api.get(`orders/${orderId}/transaction`)
                        .then((txResponse) => {
                            if (cancelled) {
                                return;
                            }
                            const tx = txResponse.data?.data?.transaction;
                            if (tx && (tx.id || tx.net_amount != null)) {
                                setTransaction(tx);
                            }
                        })
                        .catch(() => {
                            // Transação é opcional
                        });
                }
                setDidNotLoad(true);
                return undefined;
            })
            .catch(() => {
                if (!cancelled) {
                    setDidNotLoad(true);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [orderId]);

    useEffect(() => load(), [load]);

    const handlePrintReceipt = () => {
        if (!order) {
            return;
        }
        setLoadingPDF(true);
        generatePDF(order);
        setTimeout(() => {
            setLoadingPDF(false);
        }, 1000);
    };

    if (!orderId) {
        return null;
    }

    return (
        <Box>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ sm: 'center' }}
                spacing={1}
                sx={{ mb: 2 }}
            >
                <Typography variant="h6">
                    {title || 'Detalhes da venda'}
                    {` #${presentUuid(orderId)}`}
                </Typography>
                {showPrintButton ? (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={loadingPDF ? <Downloading /> : <Print />}
                        onClick={handlePrintReceipt}
                        disabled={loadingPDF || !order}
                    >
                        {loadingPDF ? 'Gerando…' : 'Imprimir comprovante'}
                    </Button>
                ) : null}
            </Stack>

            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            )}
            {!isLoading && didNotLoad && (
                <Typography color="error">
                    Não foi possível carregar os detalhes desta venda.
                </Typography>
            )}
            {!isLoading && !didNotLoad && order && (
                <OrderDetailsBody
                    order={order}
                    transaction={transaction}
                    onOrderUpdated={setOrder}
                />
            )}
        </Box>
    );
}
